/**
 * Favorites — Default Library landing page (Table View)
 *
 * Shows the user's personally starred items — documents (articles included)
 * and folders, decision 38 — in a clean table.
 * Per-user curation — reduces overwhelm by showing only what matters.
 * Folders show as expandable rows with their contents inline.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import {
  Star, Search, FileText,
  Folder, FolderOpen, AlertTriangle, Eye, Download, Edit, Trash2,
  MoreVertical, ChevronRight, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StarButton } from '@/components/library/star-button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { readLibraryFolders } from '@/lib/use-library-folders';
import {
  useFavorites, unstar, useStarWriteInFlight, favoriteDisplayKind, favoriteOpenTarget, favoriteStarTarget,
} from '@/lib/favorites-store';
import { DocumentKindBadge, DocumentKindIcon } from '@/components/library-documents-shared/document-kind';
import { DocumentKindSchema, type DocumentKind } from '@argusai/shared';
import type { StarredItem, WikiArticleRow } from '@/types/library';

export const Route = createFileRoute('/library/')({
  component: FavoritesPage,
});

// ─── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// Decision 19 (LB3-T5) — a document favourite is shown by its document's
// STORED kind, which the API serves on the row (`documentKind`; an article
// favourite is a document of kind 'article': the article icon, and it opens
// the reader; a synced one is Synced, as on the Documents list). The icon and
// the type badge are the ones the Documents list draws
// (components/library-documents-shared/document-kind).

const ICON_TILE: Record<DocumentKind | 'folder', string> = {
  article: 'bg-purple-500/10',
  synced: 'bg-sky-500/10',
  folder: 'bg-amber-500/10',
  file: 'bg-muted',
};

function typeBadge(item: StarredItem) {
  const kind = favoriteDisplayKind(item);
  if (kind === 'folder') {
    return <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">Folder</Badge>;
  }
  return <DocumentKindBadge kind={kind} />;
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  uploaded: { label: 'Uploaded', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  wiki: { label: 'Wiki', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20' },
  onedrive: { label: 'OneDrive', color: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20' },
  google_drive: { label: 'Google Drive', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  sharepoint: { label: 'SharePoint', color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20' },
  s3: { label: 'S3', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  dropbox: { label: 'Dropbox', color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20' },
};

function sourceBadge(source: string) {
  const config = SOURCE_LABELS[source] ?? { label: source, color: '' };
  return <Badge variant="outline" className={cn('text-[9px]', config.color)}>{config.label}</Badge>;
}

function statusBadge(status: string) {
  switch (status) {
    case 'ready': case 'published': return <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">{status === 'ready' ? 'Ready' : 'Published'}</Badge>;
    case 'draft': return <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">Draft</Badge>;
    case 'failed': return <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20">Failed</Badge>;
    default: return null;
  }
}

// W2-H-03 — Folder contents are now fetched from the real wiki API.
// MOCK_FOLDER_CONTENTS was removed per the "No mock data in prod" rule.

interface FolderArticle {
  id: string;
  title: string;
  status: string;
  authorName: string | null;
  updatedAt: string;
}

interface FolderSubfolder {
  id: string;
  name: string;
  documentCount: number;
}

interface FolderContents {
  articles: FolderArticle[];
  subfolders: FolderSubfolder[];
  loading: boolean;
  error: string | null;
}

const EMPTY_CONTENTS: FolderContents = {
  articles: [],
  subfolders: [],
  loading: false,
  error: null,
};

/**
 * The Type filter's label for each Document kind on this page. A Record over
 * DocumentKindSchema: the options are the schema's kinds, never a list
 * written out here (Library LB6-T2, L6-19); a folder favourite adds
 * "Folders". Exported for the filter-options test.
 */
export const FAVORITE_KIND_FILTER_LABEL: Readonly<Record<DocumentKind, string>> = Object.freeze({
  file: 'Files',
  article: 'Articles',
  synced: 'Synced',
});

/**
 * A row's "Unstar" menu item. The row's own kind goes with the unstar, so the
 * DELETE names the favourite's kind (a folder favourite is kind 'folder', not
 * 'document'); the toast comes after the server answers (LB6-T5). It goes
 * through the store's one in-flight guard, shared with the row's StarButton:
 * while either has a write of this favourite in flight the item is disabled
 * and asks nothing (LB6-T5-BF-02).
 */
function UnstarMenuItem({ item }: { item: StarredItem }) {
  const target = favoriteStarTarget(item);
  const busy = useStarWriteInFlight(target);
  return (
    <DropdownMenuItem
      disabled={busy}
      onClick={e => { e.stopPropagation(); void unstar(target); }}
      className="text-xs text-amber-600 dark:text-amber-400"
    >
      <Star className="h-3.5 w-3.5 mr-2" /> Unstar
    </DropdownMenuItem>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function FavoritesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Favorites are now backed by /api/v1/library/favorites (per UI bug
  // 2026-04-29 fix). The hook returns the rows (`items`) PLUS the
  // underlying useQuery state (`isLoading`, `isError`,
  // `refetch`) so we can show a skeleton on first load and a retry
  // button on failure rather than a misleading empty state.
  const { items, isLoading, isError, error, refetch } = useFavorites();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'starredAt' | 'updatedAt' | 'title'>('starredAt');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // W2-H-03 — folder-id → fetched contents. Cache survives panel close
  // so a re-expand is instant without another network round-trip.
  const [folderContents, setFolderContents] = useState<Record<string, FolderContents>>({});

  // When a starred folder is expanded, fetch its real contents from the
  // wiki API. Subfolders come from /wiki/folders (filtered to this parent
  // since the endpoint doesn't accept a parentId query); articles come
  // from /wiki/articles?folderId={id}. Both endpoints are tenant-scoped
  // server-side via RLS.
  useEffect(() => {
    const folderItems = items.filter((i) => i.itemType === 'folder');
    folderItems.forEach((item) => {
      // item.itemId = the wiki folder UUID, NOT the StarredItem.id.
      if (!expandedFolders.has(item.id)) return;
      // Already fetched (or loading) — skip.
      if (folderContents[item.itemId]) return;

      setFolderContents((prev) => ({
        ...prev,
        [item.itemId]: { articles: [], subfolders: [], loading: true, error: null },
      }));

      void (async () => {
        try {
          // Decision 18 — sub-folders come from the one folder list
          // (lib/use-library-folders.ts). Each carries its `count`: the
          // documents of every kind (files, synced documents, articles) this
          // person may see in it — THE resolver's predicate on the API
          // (review LB4T3-REV-02), so a folder of uploaded files no longer
          // reads "0 items".
          const [articlesRes, folders] = await Promise.all([
            apiClient.get<{ data: WikiArticleRow[]; total: number }>(
              `/wiki/articles?folderId=${encodeURIComponent(item.itemId)}&pageSize=100`
            ),
            readLibraryFolders(queryClient),
          ]);
          const subfolders: FolderSubfolder[] = folders
            .filter((f) => f.parentId === item.itemId)
            .map((f) => ({
              id: f.id,
              name: f.name,
              documentCount: f.count ?? 0,
            }));
          const articles: FolderArticle[] = (articlesRes.data ?? []).map((a) => ({
            id: a.id,
            title: a.title,
            status: a.status,
            authorName: a.authorName,
            updatedAt: a.updatedAt,
          }));
          setFolderContents((prev) => ({
            ...prev,
            [item.itemId]: { articles, subfolders, loading: false, error: null },
          }));
        } catch (err) {
          setFolderContents((prev) => ({
            ...prev,
            [item.itemId]: {
              articles: [],
              subfolders: [],
              loading: false,
              error: err instanceof Error ? err.message : 'Failed to load folder',
            },
          }));
        }
      })();
    });
  }, [expandedFolders, items, folderContents, queryClient]);

  const filtered = useMemo(() => {
    let result = items;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(i => i.title.toLowerCase().includes(q));
    }
    if (typeFilter !== 'all') {
      result = result.filter(i => favoriteDisplayKind(i) === typeFilter);
    }
    result = [...result].sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      const aDate = sortBy === 'starredAt' ? a.starredAt : (a.updatedAt ?? a.starredAt);
      const bDate = sortBy === 'starredAt' ? b.starredAt : (b.updatedAt ?? b.starredAt);
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
    return result;
  }, [items, searchTerm, sortBy, typeFilter]);

  const handleClick = (item: StarredItem) => {
    if (item.itemType === 'folder') {
      // Toggle folder expansion
      setExpandedFolders(prev => {
        const next = new Set(prev);
        if (next.has(item.id)) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
    } else {
      const target = favoriteOpenTarget(item);
      if (target) navigate(target);
    }
  };

  const toggleFolder = (id: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Loading state — show a skeleton while the first GET is in flight.
  // Critically NOT a fallback to mock data: per the no-mock-data rule
  // we'd rather show an honest skeleton than fake content.
  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-6 w-40 rounded bg-muted animate-pulse" />
            <div className="mt-2 h-3 w-24 rounded bg-muted animate-pulse" />
          </div>
        </div>
        <div className="rounded-lg border">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="grid grid-cols-[40px_minmax(220px,1fr)_90px_80px_80px_100px_100px_40px] gap-3 p-3 items-center border-b min-w-[820px]"
            >
              <div className="h-4 w-4 rounded bg-muted animate-pulse" />
              <div className="flex items-center gap-3">
                <div className="h-7 w-7 rounded bg-muted animate-pulse" />
                <div className="space-y-1">
                  <div className="h-3.5 w-48 rounded bg-muted animate-pulse" />
                  <div className="h-2.5 w-32 rounded bg-muted animate-pulse" />
                </div>
              </div>
              <div className="h-4 w-16 rounded bg-muted animate-pulse" />
              <div className="h-4 w-12 rounded bg-muted animate-pulse" />
              <div className="h-4 w-14 rounded bg-muted animate-pulse" />
              <div className="h-4 w-20 rounded bg-muted animate-pulse" />
              <div className="h-4 w-16 rounded bg-muted animate-pulse" />
              <div />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state — surface the failure honestly with a retry. NOT a
  // silent fallback to mock data.
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
        <h2 className="text-lg font-semibold">Couldn't load favorites</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          {error instanceof Error ? error.message : 'Network error.'}
        </p>
        <Button className="mt-6 gap-1.5" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  // Empty state — only shown after a successful fetch returned 0 rows.
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Star className="h-12 w-12 text-muted-foreground/20 mb-4" />
        <h2 className="text-lg font-semibold">Star the items you use most</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          Click the star icon on any document, article, or folder to see it here. Your favorites are personal — only you see them.
        </p>
        <Button className="mt-6 gap-1.5" onClick={() => navigate({ to: '/library/documents' })}>
          <FileText className="h-4 w-4" /> Browse Documents
        </Button>
        <div className="flex items-center gap-6 mt-8 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-500" /> Star a document in the Documents list or on its page</div>
          <div className="flex items-center gap-1.5"><Folder className="h-3.5 w-3.5 text-amber-500" /> Star a folder in the Documents sidebar</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500 fill-amber-500" /> Favorites
          </h2>
          <p className="text-xs text-muted-foreground">{items.length} starred item{items.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search favorites..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger aria-label="Filter favorites by type" className="w-28 h-9 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DocumentKindSchema.options.map((kind) => (
              <SelectItem key={kind} value={kind}>{FAVORITE_KIND_FILTER_LABEL[kind]}</SelectItem>
            ))}
            <SelectItem value="folder">Folders</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={v => setSortBy(v as typeof sortBy)}>
          <SelectTrigger aria-label="Sort favorites" className="w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="starredAt">Recently Starred</SelectItem>
            <SelectItem value="updatedAt">Recently Updated</SelectItem>
            <SelectItem value="title">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table — overflow-x-auto + min-w on rows so the title column stays
          full-width and the action column stays reachable on tablet (≤768px).
          Per UI audit 2026-04-29 HIGH-4 / HIGH-5. */}
      <div className="rounded-lg border overflow-x-auto">
        <div className="grid grid-cols-[40px_minmax(220px,1fr)_90px_80px_80px_100px_100px_40px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider min-w-[820px]">
          <span></span>
          <span>Name</span>
          <span>Source</span>
          <span>Type</span>
          <span>Status</span>
          <span>Author</span>
          <span>Starred</span>
          <span></span>
        </div>
        <div className="divide-y">
          {filtered.map(item => {
            const displayKind = favoriteDisplayKind(item);
            const isFolder = displayKind === 'folder';
            const isFolderExpanded = expandedFolders.has(item.id);

            return (
              <div key={item.id}>
                {/* Main row */}
                <div
                  className={cn(
                    'grid grid-cols-[40px_minmax(220px,1fr)_90px_80px_80px_100px_100px_40px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group min-w-[820px]',
                    item.syncError && 'border-l-2 border-l-amber-500',
                  )}
                  onClick={() => handleClick(item)}
                >
                  {/* Star */}
                  <StarButton item={favoriteStarTarget(item)} />

                  {/* Name + icon */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn('p-1.5 rounded shrink-0', displayKind ? ICON_TILE[displayKind] : 'bg-muted')}>
                      {displayKind === 'folder' ? (
                        isFolderExpanded ? <FolderOpen className="h-4 w-4 text-amber-500 dark:text-amber-400" /> : <Folder className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                      ) : <DocumentKindIcon kind={displayKind} className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {isFolder && (
                          <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform shrink-0', isFolderExpanded && 'rotate-90')} />
                        )}
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        {item.syncError && (
                          <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400 shrink-0" title={item.syncError}>
                            <AlertTriangle className="h-3 w-3" />
                            <span className="text-[9px]">Sync Error</span>
                          </div>
                        )}
                      </div>
                      {item.fileName && <p className="text-[10px] text-muted-foreground truncate">{item.fileName}</p>}
                    </div>
                  </div>

                  {/* Source */}
                  {sourceBadge(item.source)}

                  {/* Type */}
                  {typeBadge(item)}

                  {/* Status */}
                  <div>{statusBadge(item.status)}</div>

                  {/* Author */}
                  <span className="text-xs text-muted-foreground truncate">{item.author ?? '—'}</span>

                  {/* Starred date */}
                  <span className="text-[10px] text-muted-foreground">{timeAgo(item.starredAt)}</span>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" title="More actions">
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isFolder && (
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); handleClick(item); }} className="text-xs">
                          <Eye className="h-3.5 w-3.5 mr-2" /> View
                        </DropdownMenuItem>
                      )}
                      {isFolder && (
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate({ to: '/library/documents' }); }} className="text-xs">
                          <FolderOpen className="h-3.5 w-3.5 mr-2" /> Open in Documents
                        </DropdownMenuItem>
                      )}
                      {/* Download removed — it fired a "Download started" toast but
                          streamed nothing. Re-add wired to the real doc-download
                          endpoint (blob + filename) when that lands. */}
                      <UnstarMenuItem item={item} />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Expanded folder contents — real /wiki/folders + /wiki/articles */}
                {isFolder && isFolderExpanded && (() => {
                  const contents = folderContents[item.itemId] ?? EMPTY_CONTENTS;
                  return (
                    <div className="bg-muted/10 border-t">
                      {contents.loading && (
                        <div className="px-3 pl-14 py-3 flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading folder…
                        </div>
                      )}
                      {!contents.loading && contents.error && (
                        <div className="px-3 pl-14 py-3 text-xs text-amber-600 dark:text-amber-400">
                          Failed to load folder: {contents.error}
                        </div>
                      )}
                      {!contents.loading && !contents.error && contents.subfolders.length === 0 && contents.articles.length === 0 && (
                        <div className="px-3 pl-14 py-3 text-xs text-muted-foreground">
                          This folder is empty.
                        </div>
                      )}
                      {!contents.loading && !contents.error && contents.subfolders.map(sf => (
                        <div
                          key={`sub-${sf.id}`}
                          className="grid grid-cols-[40px_minmax(220px,1fr)_90px_80px_80px_100px_100px_40px] gap-3 py-2.5 px-3 pl-14 items-center hover:bg-muted/20 transition-colors cursor-pointer min-w-[820px]"
                          onClick={() => navigate({ to: '/library/documents' })}
                        >
                          <div />
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-1 rounded bg-amber-500/10 shrink-0">
                              <Folder className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
                            </div>
                            <p className="text-xs truncate">{sf.name}</p>
                          </div>
                          {sourceBadge('wiki')}
                          <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">Folder</Badge>
                          <div><span className="text-[10px] text-muted-foreground">{sf.documentCount} item{sf.documentCount === 1 ? '' : 's'}</span></div>
                          <span className="text-[10px] text-muted-foreground">—</span>
                          <span className="text-[10px] text-muted-foreground">—</span>
                          <div />
                        </div>
                      ))}
                      {!contents.loading && !contents.error && contents.articles.map(a => (
                        <div
                          key={`art-${a.id}`}
                          className="grid grid-cols-[40px_minmax(220px,1fr)_90px_80px_80px_100px_100px_40px] gap-3 py-2.5 px-3 pl-14 items-center hover:bg-muted/20 transition-colors cursor-pointer min-w-[820px]"
                          onClick={() => navigate({ to: '/library/article/$articleId', params: { articleId: a.id } })}
                        >
                          <div />
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-1 rounded bg-purple-500/10 shrink-0">
                              <DocumentKindIcon kind="article" className="h-3.5 w-3.5" />
                            </div>
                            <p className="text-xs truncate">{a.title}</p>
                          </div>
                          {sourceBadge('wiki')}
                          <DocumentKindBadge kind="article" />
                          <div>{statusBadge(a.status)}</div>
                          <span className="text-xs text-muted-foreground truncate">{a.authorName ?? '—'}</span>
                          <span className="text-[10px] text-muted-foreground">{timeAgo(a.updatedAt)}</span>
                          <div />
                        </div>
                      ))}
                      {!contents.loading && !contents.error && (contents.subfolders.length > 0 || contents.articles.length > 0) && (
                        <div className="px-3 pl-14 py-2 border-t">
                          <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground h-6" onClick={() => navigate({ to: '/library/documents' })}>
                            View all in Documents →
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="p-12 text-center">
              <Star className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No favorites match your filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
